import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Modal,
  Tooltip,
  Fade,
  Collapse,
} from "@mui/material";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from "framer-motion";
import {
  logGameEnded,
  logGameShared,
  logGuessMade,
  logArticleLinkClicked,
  logFeedbackGiven,
} from "../../analytics";
import { GameHeader } from "../WordyVerse/Components/GameBoard/GameHeader";
import Footer from "../../components/Footer";
import { checkWordValidity } from "../../utils/dictionary"; // Import dictionary check utility
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import {
  Send as SendIcon,
  Newspaper,
  HelpCircle,
  ChevronDown,
  Check,
} from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Confetti from "react-confetti";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";

const BaseKeyword = ({
  title = "Keyword",
  customTitle,
  iconPath,
  subtitle = "Slide each word horizontally until the center letters line up to reveal the keyword.",
  shareText = "I played Keyword",
  shareUrl = window.location.href,
  bannerPath,
  publicationId = null,
  publicationName = "Oaklandside article",
  buttonColor = "black",
  modalBackgroundColor = "white",
  titleColor = "black",
  backgroundColor = "white",
  // New props for date-based gameplay
  getWordForDate = () => null,
  findLatestAvailableDate = () => null,
  initialDate = new Date(),
}) => {
  // State for storing guesses
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [invalidWordError, setInvalidWordError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [shakeAnimation, setShakeAnimation] = useState(false);
  const isAnimating = useRef(false);

  // State for current date and game data
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [currentGameData, setCurrentGameData] = useState(null);
  const [wordList, setWordList] = useState([]);
  const [answer, setAnswer] = useState("");
  const [articleTitle, setArticleTitle] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  // Add state for victory modal
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState(200);

  // Add state for how-to-play modal
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  // Create individual motion values at the top level
  const motion1 = useMotionValue(0);
  const motion2 = useMotionValue(0);
  const motion3 = useMotionValue(0);
  const motion4 = useMotionValue(0);
  const motion5 = useMotionValue(0);

  // Collect all motion values in an array we can reference
  const motionValues = useMemo(() => {
    return [motion1, motion2, motion3, motion4, motion5];
  }, [motion1, motion2, motion3, motion4, motion5]);

  // Create refs for each word's dimensions
  const wordRefs = useRef(
    Array(wordList.length)
      .fill()
      .map((_, i) => ({
        x: motionValues[i],
        width: 0,
        letterWidth: 0,
      }))
  );

  // State for storing the width of each letter cell
  const [letterWidth, setLetterWidth] = useState(60);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const isInitialSetup = useRef(true);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  // Add state for article info visibility
  const [isArticleInfoVisible, setIsArticleInfoVisible] = useState(true);

  // Store the exact letter positions that were ever correct
  const [correctLetterPositions, setCorrectLetterPositions] = useState(
    new Set()
  );

  // Fun feedback messages for ratings
  const POSITIVE_FEEDBACK = [
    "Awesome choice! You've got great taste!",
    "Woo-hoo! \nThanks for the thumbs up!",
    "You rock! Thanks for the positive vibes!",
    "High five! We're glad you enjoyed it!",
    "Fantastic! Your feedback makes our day!",
  ];
  const NEGATIVE_FEEDBACK = [
    "Thanks for the honesty! We'll do better next time!",
    "Gotcha! We'll mix it up tomorrow!",
    "Challenge accepted! We'll find better words!",
    "Your feedback helps us improve! Thanks!",
    "We hear you! Tomorrow's word will be better!",
  ];

  // Add state for rating
  const [rating, setRating] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  // Helper function to normalize dates for comparison
  const normalizeDate = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  // Helper function to get game data for a specific date
  const getGameDataForDate = (date) => {
    const normalizedDate = normalizeDate(date);
    const dateStr = formatDate(normalizedDate);
    return getWordForDate(date);
  };

  // Helper function to add UTM parameters to URLs
  const addUtmParams = (url) => {
    if (!url) return url;
    const urlObj = new URL(url);
    urlObj.searchParams.set("utm_source", "flyingcometgames");
    urlObj.searchParams.set("utm_medium", "game");
    urlObj.searchParams.set("utm_campaign", "keyword");
    return urlObj.toString();
  };

  // Effect to initialize game data when component mounts
  useEffect(() => {
    const latestGameData = findLatestAvailableDate();
    if (latestGameData) {
      setCurrentDate(new Date(latestGameData.date));
      setCurrentGameData(latestGameData);
      setWordList(
        (latestGameData.component_words || []).map((word) => word.toUpperCase())
      );
      setAnswer(latestGameData.keyword || "");
      setArticleTitle(latestGameData.article_title || "");
      setArticleUrl(latestGameData.article_url || "");
    }
  }, []); // Only run on mount

  // Effect to update game data when date changes
  useEffect(() => {
    const gameData = getWordForDate(currentDate);
    if (gameData) {
      setCurrentGameData(gameData);
      // Set wordList and other game data from currentGameData
      setWordList(
        (gameData.component_words || []).map((word) => word.toUpperCase())
      );
      setAnswer(gameData.keyword || "");
      setArticleTitle(gameData.article_title || "");
      setArticleUrl(gameData.article_url || "");
      // Reset game state
      setGuesses([]);
      setCurrentGuess("");
      setGameOver(false);
      setCorrectLetterPositions(new Set());
      // Reset motion values
      motionValues.forEach((motion) => motion.set(0));
    }
  }, [currentDate, getWordForDate]);

  // Date navigation handler
  const changeDate = (increment) => {
    if (!currentDate) return;

    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + increment);

    // Check if we have game data for the new date
    const gameData = getWordForDate(newDate);
    if (gameData) {
      setCurrentDate(newDate);
    }
  };

  // Show how-to-play dialog when component mounts
  useEffect(() => {
    setShowHowToPlay(true);
  }, []);

  // Calculate the appropriate dimensions on mount and window resize
  useEffect(() => {
    const calculateDimensions = () => {
      if (!containerRef.current || !wordList || wordList.length === 0) {
        return;
      }
      // Hide content while calculating
      if (isInitialSetup.current) {
        setIsLayoutReady(false);
      }

      const width = containerRef.current.offsetWidth;
      setContainerWidth(width);

      // Calculate letter width based on the longest word
      const maxWordLength = Math.max(...wordList.map((word) => word.length));
      const calculatedWidth = Math.min(60, (width - 40) / maxWordLength);
      setLetterWidth(calculatedWidth);

      // Update refs with new dimensions
      wordRefs.current = wordRefs.current.map((ref, index) => {
        const wordLength = wordList[index].length;
        return {
          ...ref,
          width: wordLength * calculatedWidth,
          letterWidth: calculatedWidth,
        };
      });

      // If this is the initial setup, position words to have specific letters in center
      if (isInitialSetup.current) {
        isInitialSetup.current = false;

        // Position words immediately but keep content hidden
        const centerOffset = width / 2 - calculatedWidth / 2;

        // Set each word to align the correct center letter (left-middle for even, true center for odd)
        wordList.forEach((word, index) => {
          const wordLength = word.length;
          // For even, pick left-middle; for odd, pick true center
          const centerLetterIndex =
            wordLength % 2 === 0
              ? wordLength / 2 - 1
              : Math.floor(wordLength / 2);
          const targetPosition =
            centerOffset - calculatedWidth * centerLetterIndex;
          motionValues[index].set(targetPosition);
        });

        // Short delay to ensure DOM updates, then show content
        setTimeout(() => {
          setIsLayoutReady(true);
        }, 100);
      }
    };

    calculateDimensions();

    window.addEventListener("resize", calculateDimensions);
    return () => window.removeEventListener("resize", calculateDimensions);
  }, [wordList, motionValues]);

  // Sync wordRefs.current to wordList length and letterWidth
  useEffect(() => {
    wordRefs.current = Array(wordList.length)
      .fill()
      .map((_, i) => ({
        x: motionValues[i],
        width: 0,
        letterWidth: letterWidth,
      }));
  }, [wordList, motionValues, letterWidth]);

  // Snap position to closest letter grid after drag ends
  const snapToGrid = (index, position) => {
    if (!wordRefs.current[index]) return; // guard
    const letterW = wordRefs.current[index].letterWidth;
    const centerX = containerWidth / 2;

    // Calculate offset from the exact center of the grid
    const offsetFromCenter = centerX - letterW / 2 - position;

    // Calculate how many letter steps away we are
    const letterSteps = Math.round(offsetFromCenter / letterW);

    // Calculate target position to snap to grid
    const snapPosition = centerX - letterW / 2 - letterSteps * letterW;

    // Word constraints to ensure a letter is always in center column
    const wordLength = wordList[index].length;
    const maxRight = centerX - letterW / 2; // First letter at center
    const maxLeft = maxRight - (wordLength - 1) * letterW; // Last letter at center

    // Ensure position is within bounds
    const clampedPosition = Math.min(Math.max(snapPosition, maxLeft), maxRight);

    // Animate to the snapped position
    animate(motionValues[index], clampedPosition, {
      type: "spring",
      damping: 20,
      stiffness: 300,
    });

    return clampedPosition;
  };

  // Extract the current center letter from each word based on its position
  const extractGuess = () => {
    const centerLetters = wordList.map((word, index) => {
      const xPos = motionValues[index].get();
      const letterW = wordRefs.current[index].letterWidth;
      const centerX = containerWidth / 2;

      // Calculate which letter is in the center
      // For the first letter to be at center, xPos = centerX - letterW/2
      // So letter index = (centerX - letterW/2 - xPos) / letterW
      const letterIndex = Math.round((centerX - letterW / 2 - xPos) / letterW);

      // Ensure index is within bounds
      if (letterIndex >= 0 && letterIndex < word.length) {
        return word[letterIndex];
      }
      return "";
    });

    return centerLetters.join("");
  };

  // Effect to handle victory celebration with confetti
  useEffect(() => {
    if (
      gameOver &&
      guesses.length > 0 &&
      guesses[guesses.length - 1] === answer
    ) {
      setShowVictoryModal(true);
      setShowConfetti(true);

      // Gradually reduce confetti pieces for a fade-out effect
      const duration = 4000;
      const fadeOutTime = 1000;
      const fadeOutInterval = 50;
      const decrementSteps = fadeOutTime / fadeOutInterval;
      const decrementAmount = Math.ceil(200 / decrementSteps);

      // Start the fade-out after the duration
      const timer = setTimeout(() => {
        const fadeInterval = setInterval(() => {
          setConfettiPieces((prev) => {
            const newValue = prev - decrementAmount;
            if (newValue <= 0) {
              clearInterval(fadeInterval);
              setShowConfetti(false);
              return 0;
            }
            return newValue;
          });
        }, fadeOutInterval);
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [gameOver, guesses, answer]);

  // Check the guess and update the game state
  const checkGuess = async () => {
    const extractedGuess = extractGuess();

    if (extractedGuess.length === wordList.length) {
      // Check if the guess is a valid word
      try {
        const isValid = await checkWordValidity(extractedGuess.toLowerCase());

        if (!isValid) {
          // Only start shake animation if not already animating
          if (!isAnimating.current) {
            isAnimating.current = true;
            setInvalidWordError(true);
            setErrorMessage("Not a valid word!");
            setShakeAnimation(true);

            // Perform shake animation on each word
            wordList.forEach((_, index) => {
              const currentX = motionValues[index].get();

              // Create a shake sequence using animate
              animate(
                motionValues[index],
                [
                  currentX,
                  currentX - 10,
                  currentX + 10,
                  currentX - 10,
                  currentX + 10,
                  currentX,
                ],
                { duration: 0.4, ease: "easeInOut" }
              );
            });

            // Reset shake animation after it completes
            setTimeout(() => {
              setShakeAnimation(false);

              // Make sure all words are properly snapped back to grid
              wordList.forEach((_, index) => {
                snapToGrid(index, motionValues[index].get());
              });

              // Reset animation state
              isAnimating.current = false;
            }, 500);

            // Reset error message after a delay
            setTimeout(() => {
              setInvalidWordError(false);
            }, 3000);
          }
          return;
        }

        // Update correct letter positions - add any new correct positions to the existing set
        const newCorrectLetterPositions = new Set(correctLetterPositions);

        // For each word, check which letter is in the center and mark its position if correct
        wordList.forEach((word, wordIndex) => {
          const xPos = motionValues[wordIndex].get();
          const letterW = wordRefs.current[0].letterWidth;
          const centerX = containerWidth / 2;
          const letterIndex = Math.round(
            (centerX - letterW / 2 - xPos) / letterW
          );

          if (letterIndex >= 0 && letterIndex < word.length) {
            // If this letter matches the answer at this position, mark its exact position as correct
            if (
              wordIndex < answer.length &&
              word[letterIndex].toUpperCase() ===
                answer[wordIndex].toUpperCase()
            ) {
              newCorrectLetterPositions.add(`${wordIndex}-${letterIndex}`);
            }
          }
        });

        setCorrectLetterPositions(newCorrectLetterPositions);
        setCurrentGuess(extractedGuess);
        const newGuesses = [...guesses, extractedGuess];
        setGuesses(newGuesses);

        const isCorrect = extractedGuess === answer;

        if (isCorrect) {
          setGameOver(true);

          // Log game ended analytics
          logGameEnded("Keyword", publicationId, {
            won: isCorrect,
            attempts: newGuesses.length,
            word: answer,
            date: new Date().toLocaleDateString("en-US"),
          });
        }

        // Log guess made
        logGuessMade("Keyword", publicationId, {
          attempt: newGuesses.length,
          word: answer,
          isCorrect,
          date: new Date().toLocaleDateString("en-US"),
        });
      } catch (error) {
        console.error("Error validating word:", error);
        // If validation fails, still accept the word
        setCurrentGuess(extractedGuess);
        const newGuesses = [...guesses, extractedGuess];
        setGuesses(newGuesses);

        const isCorrect = extractedGuess === answer;
        if (isCorrect) {
          setGameOver(true);
        }
      }
    }
  };

  // Highlight letters based on their position in the answer
  const getLetterColor = (letter, position) => {
    if (!letter) return ["white", "black"];

    if (position < answer.length && letter === answer[position]) {
      return ["#91B2D1", "black"]; // Correct position color (light blue)
    }

    return ["white", "black"]; // Default color
  };

  // Get color for displayed guesses
  const getGuessLetterColor = (letter, position, guess) => {
    // If the entire guess is correct, highlight all letters
    if (guess === answer) {
      return ["#91B2D1", "black"]; // All letters in light blue
    }

    // For incorrect guesses, only highlight matching letters
    if (position < answer.length && letter === answer[position]) {
      return ["#91B2D1", "black"]; // Matching letter in light blue
    }

    // Default for non-matching letters in incorrect guesses
    return ["white", "black"]; // White background with black text
  };

  // Share functionality
  const copyToClipboard = (text) => {
    return navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowShareToast(true);
        setTimeout(() => {
          setShowShareToast(false);
        }, 3000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleShare = () => {
    const dateStr = currentDate.toLocaleDateString("en-US");
    const won = guesses.length > 0 && guesses[guesses.length - 1] === answer;

    // Format: "Oaklandside Keyword 3/24/2025"
    let shareString = `${customTitle || title} Keyword ${dateStr}\n\n`;

    // Add emoji representation of guesses - lock for wrong, key for correct
    guesses.forEach((guess) => {
      shareString += guess === answer ? "🗝️" : "🔒";
    });

    shareString += "\n\nPlay at: " + shareUrl;

    copyToClipboard(shareString)
      .then(() => {
        // Log share analytics
        logGameShared(title, publicationId, {
          won: won,
          attempts: guesses.length,
          word: answer,
          date: dateStr,
        });
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  // Get the current selected letter index for highlighting
  const getSelectedLetterIndex = (wordIndex) => {
    if (!containerRef.current) return 0;

    const xPos = motionValues[wordIndex].get();
    const letterW = wordRefs.current[wordIndex].letterWidth;
    const centerX = containerWidth / 2;

    // Calculate which letter is at center
    const letterIndex = Math.round((centerX - letterW / 2 - xPos) / letterW);

    // Ensure we return a valid index
    if (letterIndex < 0) return 0;
    if (letterIndex >= wordList[wordIndex].length)
      return wordList[wordIndex].length - 1;

    return letterIndex;
  };

  // Reset layout when wordList changes (e.g., when date changes)
  useEffect(() => {
    isInitialSetup.current = true;
    setIsLayoutReady(false);
  }, [wordList]);

  // Add handleRating function
  const handleRating = (isPositive) => {
    setRating(isPositive);
    setHasVoted(true);
    // Set a random feedback message based on rating
    const messages = isPositive ? POSITIVE_FEEDBACK : NEGATIVE_FEEDBACK;
    const randomIndex = Math.floor(Math.random() * messages.length);
    setFeedbackMessage(messages[randomIndex]);
    // Log feedback
    logFeedbackGiven(title, isPositive, publicationId, {
      won: true,
      word: answer,
      date: currentDate.toLocaleDateString("en-US"),
    });
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        p: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        height: "100%",
        minHeight: "500px",
        width: "100%",
        backgroundColor: backgroundColor,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        {bannerPath && (
          <GameHeader
            title={customTitle || title}
            subtitle={subtitle}
            iconPath={iconPath}
            bannerPath={bannerPath}
            height="100px"
            width="350px"
          />
        )}

        {/* How to Play Dialog */}
        <Dialog
          open={showHowToPlay}
          onClose={() => {
            setShowHowToPlay(false);
          }}
          maxWidth="sm"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 0,
              m: 2,
              maxWidth: { xs: "90%", sm: "400px" },
              position: "relative",
              overflow: "visible",
              bgcolor: "white",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pt: 3,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              position: "relative",
            }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: "1.5rem",
                color: "black",
              }}
            >
              How to play
            </Typography>
            <IconButton
              onClick={() => setShowHowToPlay(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "black",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 4, pb: 4, pt: 1 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                  }}
                >
                  <img
                    src="/assets/icons/slide.svg"
                    alt="Slide icon"
                    style={{
                      width: "32px",
                      height: "32px",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Drag words horizontally to form center word, aligned
                  vertically.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box sx={{ mr: 2 }}>
                  <Check size={24} />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Tap "Check" to submit your guess.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                    flexShrink: 0, // Prevent the box from shrinking
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: "#91B2D1",
                      border: "1px solid black",
                      borderRadius: 0,
                      flexShrink: 0, // Prevent the square from shrinking
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  If the letter is in the correct place, the tile will turn
                  blue.
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={() => setShowHowToPlay(false)}
              sx={{
                bgcolor: buttonColor,
                color: "white",
                borderRadius: "24px",
                py: 1.5,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "black",
                  opacity: 0.9,
                },
              }}
            >
              Play!
            </Button>
          </DialogContent>
        </Dialog>

        {/* Victory Modal */}
        <Dialog
          open={showVictoryModal}
          onClose={() => setShowVictoryModal(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              overflow: "hidden",
              bgcolor: modalBackgroundColor,
              color: "white",
              borderRadius: "12px",
              textAlign: "left",
              p: { xs: 3, sm: 4 },
              m: 2,
              maxWidth: { xs: "90%", sm: "400px" },
              width: "100%",
              border: "1px solid black",
              position: "relative",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            {showConfetti && (
              <Confetti
                width={containerWidth || 400}
                height={500}
                recycle={true}
                numberOfPieces={confettiPieces}
                gravity={0.15}
                opacity={confettiPieces < 100 ? confettiPieces / 100 : 1}
                confineToParentElement={true}
                colors={[
                  "#FFA500",
                  "#FF4500",
                  "#FFD700",
                  "#FFFF00",
                  "#00BFFF",
                  "#1E90FF",
                  "#ADD8E6",
                ]}
              />
            )}

            <IconButton
              onClick={() => setShowVictoryModal(false)}
              aria-label="close"
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "white",
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "rotate(90deg)",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Victory Message */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  pt: 3,
                  mt: 3,
                  fontWeight: "bold",
                  color: "white",
                  fontSize: { xs: "2.2rem", sm: "2.2rem" },
                  textAlign: "center",
                }}
              >
                YOU SOLVED IT!
              </Typography>
            </motion.div>

            {/* Display the answer */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <Box sx={{ mb: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  {Array.from(answer).map((letter, index) => (
                    <motion.div
                      key={index}
                      initial={{ rotateY: 90, opacity: 0 }}
                      animate={{ rotateY: 0, opacity: 1 }}
                      transition={{
                        delay: 0.3 + index * 0.1,
                        duration: 0.3,
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          border: "2px solid",
                          borderColor: "#000",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white",
                          color: "black",
                          fontSize: "2rem",
                          fontWeight: "bold",
                          // Directional shadow matching the screenshot
                          boxShadow: "4px 4px 0px rgba(0, 0, 0, 0.3)",
                          transition: "all 0.3s ease",
                          // Text styling
                          fontFamily: "'Arial', sans-serif",
                          fontWeight: 700,
                        }}
                      >
                        {letter}
                      </Box>
                    </motion.div>
                  ))}
                </Box>
              </Box>
            </motion.div>

            {/* Article title and link */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  p: 3,
                  mb: 4,
                  mx: "auto",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                  textAlign: "left",
                  color: "black",
                  border: "1px solid black", // Add black border for consistency
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", mb: 2.5 }}
                >
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: "bold",
                      color: "black",
                      fontSize: { xs: "1.1rem", sm: "1.3rem" },
                      lineHeight: 1.3,
                    }}
                  >
                    {articleTitle}
                  </Typography>
                </Box>

                <Button
                  component={Link}
                  href={addUtmParams(articleUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    logArticleLinkClicked(title, publicationId, {
                      source: "modal",
                      articleTitle,
                      articleUrl: addUtmParams(articleUrl),
                      date: currentDate.toLocaleDateString("en-US"),
                    });
                  }}
                  variant="contained"
                  sx={{
                    bgcolor: buttonColor,
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: "20px",
                    px: 3,
                    py: 1,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: buttonColor,
                      opacity: 0.9,
                      boxShadow: "none",
                    },
                  }}
                >
                  Read the article
                </Button>
              </Box>
            </motion.div>

            {/* Share Button */}
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Button
                  startIcon={<SendIcon />}
                  variant="contained"
                  onClick={handleShare}
                  sx={{
                    bgcolor: "black",
                    color: "white",
                    fontWeight: "bold",
                    borderRadius: "30px",
                    px: 4,
                    py: 1.5,
                    mb: 0,
                    width: "80%",
                    textTransform: "none",
                    transition: "all 0.2s ease",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#333",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  Share my score
                </Button>
              </Box>
            </motion.div>

            {/* Rating Section - only after solving */}
            {showVictoryModal && (
              <Box sx={{ mt: 3, mb: 2 }}>
                <AnimatePresence mode="wait">
                  {!hasVoted ? (
                    <motion.div
                      key="rating-prompt"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          fontWeight: "bold",
                          color: "white",
                          textAlign: "center",
                        }}
                      >
                        How was today's Keyword?
                      </Typography>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="feedback-message"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "white",
                        }}
                      >
                        {feedbackMessage}
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 4,
                    perspective: "1000px",
                    height: "80px",
                    alignItems: "center",
                  }}
                >
                  {/* Thumbs Up Button */}
                  <motion.div
                    whileTap={{ scale: 0.9 }}
                    animate={
                      rating === true
                        ? {
                            scale: [1, 1.5, 1],
                            rotate: [0, 15, 0],
                            transition: { duration: 0.5 },
                          }
                        : {}
                    }
                  >
                    <Box
                      component={
                        hasVoted && rating !== true ? "div" : IconButton
                      }
                      onClick={hasVoted ? undefined : () => handleRating(true)}
                      sx={{
                        bgcolor:
                          rating === true
                            ? "rgba(255, 255, 255, 0.3)"
                            : "transparent",
                        border: `2px solid white`,
                        borderRadius: "50%",
                        p: 2,
                        width: "64px",
                        height: "64px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...(hasVoted && rating !== true
                          ? { opacity: 0.5 }
                          : {
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.2)",
                              },
                            }),
                      }}
                    >
                      {rating === true ? (
                        <ThumbUpIcon style={{ color: "white", fontSize: 32 }} />
                      ) : (
                        <ThumbUpOutlinedIcon
                          style={{ color: "white", fontSize: 32 }}
                        />
                      )}
                    </Box>
                  </motion.div>
                  {/* Thumbs Down Button */}
                  <motion.div
                    whileTap={{ scale: 0.9 }}
                    animate={
                      rating === false
                        ? {
                            scale: [1, 1.5, 1],
                            rotate: [0, -15, 0],
                            transition: { duration: 0.5 },
                          }
                        : {}
                    }
                  >
                    <Box
                      component={
                        hasVoted && rating !== false ? "div" : IconButton
                      }
                      onClick={hasVoted ? undefined : () => handleRating(false)}
                      sx={{
                        bgcolor:
                          rating === false
                            ? "rgba(255, 255, 255, 0.3)"
                            : "transparent",
                        border: `2px solid white`,
                        borderRadius: "50%",
                        p: 2,
                        width: "64px",
                        height: "64px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...(hasVoted && rating !== false
                          ? { opacity: 0.5 }
                          : {
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "rgba(255, 255, 255, 0.2)",
                              },
                            }),
                      }}
                    >
                      {rating === false ? (
                        <ThumbDownIcon
                          style={{ color: "white", fontSize: 32 }}
                        />
                      ) : (
                        <ThumbDownOutlinedIcon
                          style={{ color: "white", fontSize: 32 }}
                        />
                      )}
                    </Box>
                  </motion.div>
                </Box>
              </Box>
            )}
          </DialogContent>
          {/* Toast notification for share to clipboard (modal-wide banner) */}
          <AnimatePresence>
            {showShareToast && (
              <motion.div
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#43a047",
                  color: "white",
                  fontWeight: "bold",
                  minHeight: "48px",
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 8,
                    fontSize: 15,
                    color: "#43a047",
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
                Results copied to clipboard!
              </motion.div>
            )}
          </AnimatePresence>
        </Dialog>

        {/* Article source with collapsible content */}
        <Box
          sx={{
            my: { xs: 1.5, sm: 2 },
            mx: "auto",
            textAlign: "left",
            maxWidth: "600px",
            width: "100%",
          }}
        >
          <Box
            onClick={() => setIsArticleInfoVisible(!isArticleInfoVisible)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0,
              py: { xs: 1.2, sm: 1.5 },
              px: 2.5,
              cursor: "pointer",
              borderRadius: "12px",
              backgroundColor: "white",
              transition: "all 0.2s ease",
              border: "1px solid rgba(0, 0, 0, 0.15)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              minHeight: "44px",
              position: "relative",
              ...(isArticleInfoVisible && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottom: "none",
                mb: 0,
              }),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Newspaper
                size={20}
                style={{
                  marginRight: "12px",
                  opacity: 0.7,
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  color: "rgba(0, 0, 0, 0.8)",
                  lineHeight: 1.2,
                  fontWeight: 500,
                  userSelect: "none",
                }}
              >
                Based on the article:
              </Typography>
            </Box>
            <ChevronDown
              size={20}
              style={{
                marginLeft: "12px",
                transform: isArticleInfoVisible
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.3s ease",
                opacity: 0.6,
              }}
            />
          </Box>

          <Collapse in={isArticleInfoVisible} timeout={300}>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                backgroundColor: "white",
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
                border: "1px solid rgba(0, 0, 0, 0.15)",
                borderTop: "none",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              <Link
                href={addUtmParams(articleUrl)}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
                onClick={() => {
                  logArticleLinkClicked(title, publicationId, {
                    source: "main_screen",
                    articleTitle,
                    articleUrl: addUtmParams(articleUrl),
                    date: currentDate.toLocaleDateString("en-US"),
                  });
                }}
                sx={{
                  color: "black",
                  fontSize: { xs: "0.95rem", sm: "1.1rem" },
                  fontWeight: 700,
                  display: "block",
                  textAlign: "left",
                  mr: 4,
                  textDecoration: "underline",
                  lineHeight: 1.3,
                  "&:hover": {
                    color: "black",
                    opacity: 0.75,
                  },
                  "&:active": {
                    opacity: 0.6,
                  },
                }}
              >
                {articleTitle}
              </Link>
            </Box>
          </Collapse>
        </Box>

        {/* Date Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: { xs: 1, sm: 2 },
            backgroundColor: "white",
            borderRadius: "12px",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            px: 2.5,
          }}
          maxWidth="600px"
          mx="auto"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => changeDate(-1)}
              disabled={
                !getGameDataForDate(new Date(currentDate.getTime() - 86400000))
              }
              sx={{
                color: "black",
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Typography sx={{ mx: 0, color: "black" }}>
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </Typography>
            {/* Only show ChevronRight if there is a future date */}
            {getGameDataForDate(new Date(currentDate.getTime() + 86400000)) && (
              <IconButton
                onClick={() => changeDate(1)}
                sx={{
                  color: "black",
                  "&.Mui-disabled": {
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </Box>

          {/* Game info icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setShowHowToPlay(true)}
              aria-label="How to play"
              sx={{
                color: "black",
                padding: 1,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.08)",
                },
              }}
            >
              <HelpCircle size={24} />
            </IconButton>
          </Box>
        </Box>

        {/* Word sliding area */}
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            maxWidth: "sm",
            mx: "auto",
            height: `${wordList.length * 70 + 13}px`,
            mb: 3,
            overflow: "hidden",
            borderRadius: "12px",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            backgroundColor: "white",
          }}
        >
          {/* Loading placeholder when layout is not ready */}
          {!isLayoutReady && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                opacity: 0.7,
              }}
            >
              {/* Visual placeholder for the rows */}
              {Array.from({ length: wordList.length }).map((_, index) => (
                <Box
                  key={`placeholder-${index}`}
                  sx={{
                    width: "80%",
                    height: "60px",
                    my: 1.5,
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.05)",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Conditional rendering with fade-in animation */}
          <AnimatePresence>
            {isLayoutReady && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ width: "100%", height: "100%" }}
              >
                {/* Center column - static letter boxes */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    height: "100%",
                    width: letterWidth,
                    transform: "translateX(-50%)",
                    zIndex: 1,
                    pointerEvents: "none", // Ensure it doesn't interfere with dragging
                  }}
                >
                  {/* Individual letter boxes in center column - now just borders */}
                  {wordList.map((_, wordIndex) => (
                    <Box
                      key={`center-box-${wordIndex}`}
                      sx={{
                        position: "absolute",
                        width: letterWidth,
                        height: "60px",
                        top: `${wordIndex * 70 + 10}px`,
                        border: "4px solid black",
                        borderRight: "3px solid black",
                        zIndex: 0,
                      }}
                    />
                  ))}
                </Box>

                {/* Words that can be dragged */}
                {wordList.map((word, wordIndex) => {
                  const xMotionValue = motionValues[wordIndex];
                  const wordWidth = word.length * letterWidth;

                  return (
                    <motion.div
                      key={`word-${wordIndex}`}
                      style={{
                        x: xMotionValue,
                        position: "absolute",
                        top: `${wordIndex * 70 + 10}px`,
                        height: "60px",
                        display: "flex",
                        touchAction: "none",
                        cursor: "grab",
                      }}
                      whileDrag={{ cursor: "grabbing" }}
                      drag="x"
                      dragConstraints={{
                        left: -(wordWidth - containerWidth / 2),
                        right: containerWidth / 2 - letterWidth,
                      }}
                      dragElastic={0.1}
                      dragMomentum={false}
                      onDragEnd={() =>
                        snapToGrid(wordIndex, xMotionValue.get())
                      }
                    >
                      {Array.from(word).map((letter, letterIndex) => {
                        // Check if this exact letter position was ever marked as correct
                        const isCorrectPosition = correctLetterPositions.has(
                          `${wordIndex}-${letterIndex}`
                        );

                        return (
                          <Box
                            key={`letter-${wordIndex}-${letterIndex}`}
                            sx={{
                              width: letterWidth,
                              height: "60px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isCorrectPosition
                                ? "#91B2D1"
                                : "white",
                              border: "2px solid black",
                              borderRight: "1px solid black",
                              borderLeft: "1px solid black",
                              borderBottom: "1px solid black",
                              borderTop: isCorrectPosition
                                ? "2px solid black"
                                : "1px solid black",
                              color: isCorrectPosition ? "white" : "black",
                              fontSize: "1.5rem",
                              fontWeight: isCorrectPosition ? 600 : 400,
                            }}
                          >
                            {letter}
                          </Box>
                        );
                      })}
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Check button */}
        <Button
          variant="contained"
          color="primary"
          disableRipple
          sx={{
            px: 4,
            py: 1,
            mb: 3,
            fontSize: "1.2rem",
            borderRadius: "20px",
            width: "75%",
            maxWidth: "300px",
            bgcolor: buttonColor,
            "&.MuiButton-contained": {
              bgcolor: buttonColor,
            },
            "&.Mui-disabled": {
              bgcolor: buttonColor,
              opacity: 0.6,
              color: "white",
            },
          }}
          onClick={checkGuess}
          disabled={gameOver || !isLayoutReady}
        >
          Check
        </Button>

        {/* Display guesses */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              maxWidth: "600px",
              mx: "auto",
              justifyContent: "center",
            }}
          >
            {[...guesses].reverse().map((guess, guessIndex) => (
              <Box
                key={`guess-${guessIndex}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  py: 0.5,
                  px: 1.5,
                  borderRadius: "6px",
                  backgroundColor:
                    guess === answer
                      ? "rgba(145, 178, 209, 0.1)"
                      : "transparent",
                  border: "1px solid",
                  borderColor:
                    guess === answer
                      ? "rgba(145, 178, 209, 0.5)"
                      : "rgba(0, 0, 0, 0.1)",
                  minWidth: "fit-content",
                }}
              >
                {guess === answer ? (
                  <KeyIcon sx={{ fontSize: 16, color: "#91B2D1" }} />
                ) : (
                  <LockIcon
                    sx={{ fontSize: 16, color: "rgba(0, 0, 0, 0.4)" }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: guess === answer ? 600 : 400,
                    color: guess === answer ? "black" : "rgba(0, 0, 0, 0.7)",
                    letterSpacing: "0.5px",
                  }}
                >
                  {guess}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BaseKeyword;
